import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

export const AuthCallback = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(() => t('auth.callback.processing'));
  const [details, setDetails] = useState<string>("");

  useEffect(() => {
    const run = async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const hash = window.location.hash;

        if (code) {
          setStatus(t('auth.callback.exchanging'));
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            setStatus(t('auth.callback.exchangeFailed'));
            setDetails(error.message ?? String(error));
            return;
          }
          setStatus(t('auth.callback.established'));
          window.history.replaceState({}, document.title, "/");
          window.location.href = "/";
          return;
        }

        if (hash && hash.includes("access_token")) {
          setStatus(t('auth.callback.settingFromHash'));
          const p = new URLSearchParams(hash.substring(1));
          const access_token = p.get("access_token") || undefined;
          const refresh_token = p.get("refresh_token") || undefined;
          if (access_token && refresh_token) {
            const { error } = await supabase.auth.setSession({ access_token, refresh_token });
            if (error) {
              setStatus(t('auth.callback.setFromHashFailed'));
              setDetails(error.message ?? String(error));
              return;
            }
            setStatus(t('auth.callback.established'));
            window.history.replaceState({}, document.title, "/");
            window.location.href = "/";
            return;
          }
        }

        setStatus(t('auth.callback.noTokens'));
        setDetails(t('auth.callback.noTokensDetail'));
      } catch (e: any) {
        setStatus(t('auth.callback.unexpected'));
        setDetails(e?.message ?? String(e));
      }
    };
    run();
  }, [t]);

  return (
    <main className="min-h-screen bg-gradient-bg flex items-center justify-center p-6">
      <section className="max-w-md w-full bg-card text-card-foreground rounded-xl shadow-lg p-6 space-y-3">
        <h1 className="text-xl font-semibold">{t('auth.callback.signingYouIn')}</h1>
        <p className="text-sm opacity-80">{status}</p>
        {details && (
          <pre className="text-xs bg-muted text-muted-foreground p-3 rounded-md overflow-auto">
            {details}
          </pre>
        )}
        <a href="/auth" className="underline text-primary text-sm">{t('auth.callback.backToLogin')}</a>
      </section>
    </main>
  );
};

export default AuthCallback;
